/**
 * Route-level React error boundary.
 *
 * Wraps the routed page content (<Outlet/>) so a render error in ANY page — e.g.
 * a widget handed a malformed API payload (see the CRM Dashboards pipeline-
 * velocity crash) — is caught and shown as a recoverable panel INSIDE the app
 * shell (sidebar + header stay usable), instead of bubbling to React Router's
 * default boundary which blanks the entire screen with "Unexpected Application
 * Error" and needs a manual URL navigation to recover.
 *
 * Reset: the boundary is remounted on every route change because the wrapper in
 * DashboardLayout is keyed by pathname — so navigating elsewhere clears the
 * error automatically. It also offers an in-place "Réessayer".
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';

import { reportClientError } from '@/lib/errorReporting';
import { tr } from '@/i18n';

interface Props {
  children: ReactNode;
}
interface State {
  error: Error | null;
}

export class RouteErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Diagnostics only — never re-throw, so the shell stays alive.
    console.error('[RouteErrorBoundary] page render error:', error, info.componentStack);
    reportClientError({
      message: error.message,
      stack: error.stack,
      componentStack: info.componentStack ?? undefined,
      source: 'render',
    });
  }

  private reset = () => this.setState({ error: null });

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    return (
      <div
        role="alert"
        className="mx-auto flex max-w-lg flex-col items-center gap-4 rounded-token-lg border border-border bg-surface p-8 text-center shadow-elevation-1"
      >
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-danger/10 text-danger">
          <svg
            aria-hidden="true"
            viewBox="0 0 24 24"
            className="h-6 w-6"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M12 9v4M12 17h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
          </svg>
        </span>
        <div>
          <h2 className="text-lg font-semibold text-foreground">{tr('shell.app.routeErrorBoundary.anErrorOccurredOnThisPage')}</h2>
          <p className="mt-1 text-sm text-muted">{tr('shell.app.routeErrorBoundary.theRestOfTheApplicationIs')}</p>
        </div>
        <div className="flex flex-wrap justify-center gap-2">
          <button
            type="button"
            onClick={this.reset}
            className="rounded-token bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition hover:brightness-110"
          >{tr('shell.app.routeErrorBoundary.tryAgain')}</button>
          <button
            type="button"
            onClick={() => window.history.back()}
            className="rounded-token border border-border px-4 py-2 text-sm font-medium text-foreground transition hover:bg-surface-2"
          >{tr('shell.app.routeErrorBoundary.back')}</button>
        </div>
        {import.meta.env.DEV && (
          <pre className="mt-2 max-h-40 w-full overflow-auto rounded-token bg-surface-2 p-2 text-left text-xs text-muted">
            {error.message}
          </pre>
        )}
      </div>
    );
  }
}
